import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { TextBlockCategory } from "Enums";

/**
 * Control for displaying checkboxes which have three states (True, False, Undefined).
 *
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a ThreeStateCheckbox with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class ThreeStateCheckbox extends Control {
    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<input type="checkbox" class="k-checkbox" />`;

    /**
     * @hidden
     */
    protected bindingProperty: string = "threestatechecked";

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-threestatecheckbox";

    /**
     * @hidden
     */
    protected static TRIGGERCHANGE: string = "TRIGGERCHANGE"; // also used in CustomBinding

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        if (this.jQueryElement.prop("readonly")) {
            this.jQueryElement.prop("disabled", true);
        }

        this.jQueryElement.on("click", () => this.onClick());
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
        this.jQueryElement.on("change", () => this.onChange());
        this.jQueryElement.data("checked", 0);
    }

    /**
     * Triggered when the value is changed.
     */
    private onChange() {
        if (this.jQueryElement.data(ThreeStateCheckbox.TRIGGERCHANGE)) {
            this.trigger(ControlEventTypes.Change);
        }
    }

    /**
     * Triggered when the element is clicked.
     */
    private onClick(): void {
        switch (this.jQueryElement.data("checked")) {
            // unchecked, going indeterminate
            case 0:
                this.setValue(null);
                break;

            // indeterminate, going checked
            case 1:
                this.setValue(true);
                break;

            // checked, going unchecked
            default:
                this.setValue(false);
                break;
        }
    }

    /**
     * Gets template for tooltip
     * @param bindingField
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return `#= ((${bindingField} == "null" || ${bindingField} == null) ? "${this.textBlockService.get(
            "Null",
            TextBlockCategory.global
        )}" : ((${bindingField} == "true" || ${bindingField} == true) ? "${this.textBlockService.get(
            "True",
            TextBlockCategory.global
        )}" : "${this.textBlockService.get("False", TextBlockCategory.global)}")) #`;
    }

    /**
     * Get value from the control.
     * @returns True if checked, false if unchecked, null if indeterminate.
     * @since 4.0.0
     */
    public getValue(): boolean {
        if (this.jQueryElement.data("checked") == 1) {
            return null;
        } else if (this.jQueryElement.is(":checked") === true) {
            return true;
        } else if (this.jQueryElement.is(":checked") === false) {
            return false;
        }
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this.jQueryElement;
    }

    /**
     * Set control value.
     * @param value Value to be set. True if checked, false if unchecked, null if indeterminate.
     * @since 4.0.0
     */
    public setValue(value: boolean) {
        if (value === true) {
            this.jQueryElement.data("checked", 2);
            this.jQueryElement.prop("indeterminate", false);
            this.jQueryElement.prop("checked", true);
        } else if (value === false) {
            this.jQueryElement.data("checked", 0);
            this.jQueryElement.prop("indeterminate", false);
            this.jQueryElement.prop("checked", false);
        } else {
            this.jQueryElement.data("checked", 1);
            this.jQueryElement.prop("indeterminate", true);
            this.jQueryElement.prop("checked", false);
        }

        // workaround because IE and Edge fire change event before click event.
        // so the first change event fired in IE/Edge has to be ignored (both dms-event and change-event in CustomBinding)
        // after changing, we can refire the event and specify the event to not be ignored.
        this.jQueryElement.data(ThreeStateCheckbox.TRIGGERCHANGE, true);
        this.jQueryElement.trigger("change");
        this.jQueryElement.data(ThreeStateCheckbox.TRIGGERCHANGE, false);
    }

    /**
     * Gets the display template for readonly columns and non-editable grid
     * @hidden
     */
     public getDisplayTemplate(bindingField?: string): string {
        let field = (bindingField || "").split(`"`).join("&quot;");
        
        return `<input type="checkbox" data-dms-checkboxid="${this.controlDefinition.ControlID}" class="k-checkbox k-checkbox-md dms-checkbox-label k-disabled" readonly="readonly" #= (${field} ? 'checked="checked"' : '') # />`;
        // return `<input type="checkbox" data-dms-checkboxid="${this.controlDefinition.ControlID}" class="k-checkbox dms-checkbox-label k-state-disabled #= (${field} == null ? "dms-indeterminate" : (${field} == true ? "dms-checked" : "")) #" readonly="readonly" #= (${field} ? 'checked="checked"' : '') # />`;
    }
    
    /**
     * Get the template for the grid group header.
     * @hidden
     */
    public getGroupHeaderTemplate(): string {
        return this.getTooltipTemplate("value");
    }

    /**
     * Get the display template for a binding field.
     * @hidden
     */
    public getDisplayTemplateForField(bindingField: string): string {
        return this.getTooltipTemplate(bindingField);
    }

    /**
     * Gets the datatype for the ViewModel (ie. "string", "number", etc.)
     * @hidden
     */
    public getModelDatatype(): string {
        return "boolean";
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();

        if (this.jQueryElement != null) {
            this.jQueryElement.off("click");
            this.jQueryElement.off("focusout");
            this.jQueryElement.off("change");
        }
    }
}
